import type { BundleView, SignedStep } from "../api/types.js";
import { clock } from "../lib/format.js";
import { BankCard } from "../ui/bankcard.js";
import { cancelLabel, heldCopy } from "./notices.js";

/**
 * 06 after the signature: the held part of a signed bundle, as its own card
 * (UX spec v3 §6.6 `held` · `cancel` · `cancelled`).
 *
 * The card is drawn from the SAME stored payload — only the held steps, each row
 * exactly as signed; the header total is the sum of their own amounts. It opens
 * on the question the customer just asked ("Cancel the held …"), answered in the
 * card: Cancel it (the primary — stopping money is the fastest path) or Keep it.
 * No modal, no signature; the gateway's one cancel route releases what is held.
 */
export interface HeldDeps {
  readonly api: { cancelBundle(bundleId: string, reason: string): Promise<unknown> };
  readonly bundle: BundleView;
  readonly held: readonly SignedStep[];
  /** The BANK's clock (DCTA-85 P1-2): `undefined` until the bank has said its time. */
  readonly now: () => Date | undefined;
  readonly say: (line: string) => void;
  readonly failed: (error: unknown) => void;
}

export class HeldFlow {
  readonly card: BankCard;
  readonly #d: HeldDeps;
  readonly #copy: ReturnType<typeof heldCopy>;
  readonly #count: string;
  #busy = false;
  #done = false;

  constructor(deps: HeldDeps) {
    this.#d = deps;
    // DCTA-143: the bank's flags ride with the bundle — a flagged name is said in its inert form in these sentences too.
    this.#copy = heldCopy(deps.held, deps.bundle.firewall?.data_flags ?? []);
    this.#count = deps.held.length === 1 ? "1 operation" : `${String(deps.held.length)} operations`;
    const view: BundleView = {
      ...deps.bundle,
      signing_payload: { ...deps.bundle.signing_payload, steps: deps.held },
      interventions: [],
      refused_steps: [],
    };
    this.card = new BankCard(view, { onSign: () => undefined, onEdit: () => undefined, onReask: () => undefined });
    this.card.signed();
    this.#ask();
  }

  #held(): void {
    this.card.phase({
      status: "Held",
      label: `Bank: ${this.#count}, held`,
      leaves: false,
      notices: [{ icon: "i-clock", cls: "end", ...this.#copy.held }],
      acts: [{ label: cancelLabel(this.#d.held), kind: "ghost", onSelect: () => this.#ask() }],
    });
  }

  #ask(): void {
    this.card.phase({
      status: "Held",
      label: `Bank: ${this.#count}, held — cancel it?`,
      leaves: false,
      notices: [{ icon: "i-clock", cls: "end", ...this.#copy.cancelAsk }],
      acts: [
        { label: "Cancel it", kind: "btn", onSelect: () => this.#cancel() },
        { label: "Keep it", kind: "ghost", onSelect: () => this.#held() },
      ],
    });
  }

  #cancel(): void {
    if (this.#busy || this.#done) return;
    this.#busy = true;
    this.#d.api
      .cancelBundle(this.#d.bundle.bundle_id, "customer cancelled the held part")
      .then(() => {
        this.#done = true;
        this.card.phase({
          status: "Cancelled",
          label: `Bank: ${this.#count}, cancelled`,
          leaves: false,
          notices: [{ icon: "i-alert", cls: "end", ...this.#copy.cancelled(bankClockTime(this.#d.now())) }],
          acts: [],
        });
        this.#d.say("Cancelled. Nothing was sent.");
      })
      .catch((error: unknown) => this.#d.failed(error))
      .finally(() => {
        this.#busy = false;
      });
  }
}

function bankClockTime(now: Date | undefined): string | undefined {
  return now === undefined ? undefined : clock(now.toISOString());
}
